import * as React from 'react';
import { RaRecord, useNotify, useRedirect } from 'react-admin';
import { useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Button,
    Stack,
    TextField,
    FormControl,
    MenuItem,
    Select,
    CircularProgress,
    FormHelperText,
    InputLabel,
    Autocomplete,
    Chip
} from '@mui/material';
import { Switch } from '../../components/switch/Switch.tsx'
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { Formik, Form } from 'formik';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { IconButton } from '../../components/icon-button/IconButton.tsx';
import EditIcon from '@mui/icons-material/Edit';
import { VariantGroupDialog, VariantGroup } from '../../components/dialogs/VariantGroupDialog.tsx';
import { Language } from '../../enums/Language.ts';
import TranslateService from "../../services/translate/translateService.ts";
import axiosInstance from '../../core/axiosConfig.ts';
import { WeekDay } from "../../enums/weekDay.ts";
import { Unit } from "../../enums/UnitType.ts";
import { RestaurantItemService } from "../../services/restaurantItem/restaurantItemService.ts";
import * as Sentry from '@sentry/react';

const UNITS = Object.entries(Unit).map(([id, name]) => ({
    id,
    name,
}));

const LANGUAGE = Object.entries(Language).map(([id, name]) => ({
    id,
    name,
}));

interface LinkedProduct {
    id: number;
    name: string;
}

// Backend stores variants as a flat list; a "variant group" is derived from the shared
// `category` string + rules. Regroup fetched variants for the UI, preserving order.
// Legacy variants with no category collapse into one unnamed group (shown as "Variants").
// Rationale: a "variant group" is a UI-only concept over the flat backend model (ADR 0001).
function groupVariants(variants: any[], makeKey: () => string): VariantGroup[] {
    const order: string[] = [];
    const byCategory = new Map<string, VariantGroup>();

    for (const v of variants) {
        const category = (v.category || '').trim();
        if (!byCategory.has(category)) {
            order.push(category);
            byCategory.set(category, {
                key: makeKey(),
                name: category,
                // Rules are copied onto every variant; take the group's rules from the first one.
                isRequired: v.isRequired ?? false,
                isSingleChoice: v.isSingleChoice ?? true,
                items: [],
            });
        }
        byCategory.get(category)!.items.push({
            name: v.name || '',
            size: v.size != null ? String(v.size) : '',
            price: v.price != null ? String(v.price) : '',
            posId: v.posId || '',
        });
    }

    return order.map(c => byCategory.get(c)!);
}

export default function LocationsCreateItem(): JSX.Element {
    const { t } = useTranslation();
    const translateService = new TranslateService();
    const [isTranslating, setIsTranslating] = React.useState(false);
    const [translated, setTranslated] = React.useState(false);
    const [translations, setTranslations] = React.useState<any[]>([]);
    const [isSaving, setIsSaving] = React.useState(false);
    const [categories, setCategories] = React.useState<any[]>([]);
    const [groups, setGroups] = React.useState<VariantGroup[]>([]);
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [editingGroup, setEditingGroup] = React.useState<VariantGroup | null>(null);
    const groupKeyRef = React.useRef(0);
    const makeGroupKey = () => `g${++groupKeyRef.current}`;
    const [isLoading, setIsLoading] = React.useState(false);
    const [data, setData] = React.useState<RaRecord | null>(null);
    const [attemptedSubmit, setAttemptedSubmit] = React.useState(false);

    const [fileBlob, setFileBlob] = React.useState<Blob | null>(null);
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);

    const [availableProducts, setAvailableProducts] = React.useState<LinkedProduct[]>([]);
    const [linkedProducts, setLinkedProducts] = React.useState<LinkedProduct[]>([]);
    const [productsLoading, setProductsLoading] = React.useState(false);
    const notify = useNotify();
    const redirect = useRedirect();
    const { id, itemId } = useParams();
    const isEditMode = Boolean(itemId);
    const restaurantItemService = new RestaurantItemService();

    React.useEffect(() => {
        if (!id) return;

        (async () => {
            setProductsLoading(true);
            try {
                const res = await axiosInstance.get('/menu-item/get/list', {
                    params: {
                        language: 'ENG',
                        restaurantId: id,
                    },
                });

                const products: LinkedProduct[] = res.data
                    .map((item: any) => ({
                        id: item.id,
                        name: item.name,
                    }))
                    .filter((p: LinkedProduct) => !isEditMode || p.id !== Number(itemId));

                setAvailableProducts(products);
            } catch (e) {
                console.error('Failed to load products for linking', e);
                Sentry.captureException(e);
            } finally {
                setProductsLoading(false);
            }
        })();
    }, [id, isEditMode, itemId]);

    React.useEffect(() => {
        if (isEditMode) {
            setIsLoading(true);
            (async () => {
                try {
                    const data = await restaurantItemService.getItems(itemId, "ENG");
                    setData(data);
                    setGroups(groupVariants(data.variants || [], makeGroupKey));
                    console.log('Fetched item data:', data);
                    setPreviewUrl(data.imageUrl);
                    const mappedBackendTranslations = data.translations?.map(t => ({
                        language: t.language,
                        translatedName: t.translatedName,
                        translatedDescription: t.translatedDescription
                    })) || [];

                    const fullTranslations = [
                        {
                            language: "ENG",
                            translatedName: data.name,
                            translatedDescription: data.description
                        },
                        ...mappedBackendTranslations
                    ];

                    setTranslations(fullTranslations);
                    setTranslated(true);

                    setIsLoading(false);
                } catch (error: any) {
                    notify(error.response.data.error, { type: 'error' });
                    console.error(error.response.data.error);
                    setIsLoading(false);
                    Sentry.captureException(error);
                }
            })();
        }
    }, [isEditMode, itemId]);

    const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
    };

    const handleFile = (file: File) => {
        const blob = new Blob([file], { type: file.type });
        setFileBlob(blob);
        setPreviewUrl(URL.createObjectURL(blob));
    };

    const handleRemove = () => {
        setFileBlob(null);
        setPreviewUrl(null);
    };

    const totalVariantCount = groups.reduce((sum, g) => sum + g.items.length, 0);

    const openAddGroup = () => {
        if (totalVariantCount >= 100) {
            notify("You can add up to 100 variants only", { type: "error" });
            return;
        }
        setEditingGroup(null);
        setDialogOpen(true);
    };

    const openEditGroup = (group: VariantGroup) => {
        setEditingGroup(group);
        setDialogOpen(true);
    };

    const handleSaveGroup = (group: VariantGroup) => {
        setGroups(prev => {
            const exists = prev.some(g => g.key === group.key);
            return exists ? prev.map(g => (g.key === group.key ? group : g)) : [...prev, group];
        });
    };

    const handleDeleteGroup = (key: string) => {
        setGroups(prev => prev.filter(g => g.key !== key));
    };


    const days = Object.entries(WeekDay).map(([id, name]) => ({ id, name }));

    const validationSchema = Yup.object({
        language: Yup.string().required('Language is required'),
        name: Yup.string().required('Name is required'),
        category: Yup.string().required('Category is required'),
        price: Yup.string().required('Price is required'),
        size: Yup.string().required('Amount is required'),
        description: Yup.string().required('Description is required'),
        unit: Yup.string().required('Unit type is required'),

    });

    const initialValues = {
        language: 'ENG',
        name: '',
        category: '',
        price: '',
        description: '',
        unit: '',
        size: '',
        posId: '',

        seasonalEnabled: false,
        specificDateEnabled: false,
        startSeason: '',
        endSeason: '',
        openingHours: days.reduce((acc, day) => {
            acc[day.id] = { openingTime: '00:00', closingTime: '00:00' };
            return acc;
        }, {} as Record<string, { openingTime: string; closingTime: string }>),
        dietaryRestrictions: {
            spicy: false,
            dairy_free: false,
            gluten_free: false,
            vegan: false,
            vegetarian: false,
            kosher: false,
            halal: false
        }

    };

    const initialEditValues = isEditMode && data ? {
        language: 'ENG',
        name: data.name || '',
        category: data.category.id || '',
        description: data.description || '',
        unit: data.unit || '',
        size: data.size,
        price: data.price || '',
        posId: data.posId || '',

        seasonalEnabled: false,
        specificDateEnabled: false,
        startSeason: '',
        endSeason: '',
        openingHours: days.reduce((acc, day) => {
            acc[day.id] = { openingTime: '00:00', closingTime: '00:00' };
            return acc;
        }, {} as Record<string, { openingTime: string; closingTime: string }>),
        dietaryRestrictions: {
            spicy: data.dietaryRestrictions.some(dr => dr.dietaryRestrictionType === 'SPICY'),
            dairy_free: data.dietaryRestrictions.some(dr => dr.dietaryRestrictionType === 'DAIRY_FREE'),
            gluten_free: data.dietaryRestrictions.some(dr => dr.dietaryRestrictionType === 'GLUTEN_FREE'),
            vegan: data.dietaryRestrictions.some(dr => dr.dietaryRestrictionType === 'VEGAN'),
            vegetarian: data.dietaryRestrictions.some(dr => dr.dietaryRestrictionType === 'VEGETARIAN'),
            kosher: data.dietaryRestrictions.some(dr => dr.dietaryRestrictionType === 'KOSHER'),
            halal: data.dietaryRestrictions.some(dr => dr.dietaryRestrictionType === 'HALAL'),
        }

    } : initialValues;


    React.useEffect(() => {
        if (!id) return;

        (async () => {
            try {
                const res = await axiosInstance.get('/category/list', {
                    params: {
                        locationId: id,
                        language: "ENG"
                    }
                });

                setCategories(res.data);
            } catch (e) {
                console.error('Failed to load categories', e);
                Sentry.captureException(e);
            }
        })();
    }, [id]);

    React.useEffect(() => {
        if (data?.suggestedProducts?.length) {
            setLinkedProducts(
                data.suggestedProducts.map((sp: any) => ({
                    id: sp.suggestedId,
                    name: sp.name,
                }))
            );
        }
    }, [data, availableProducts]);

    const handleSubmit = async (values: typeof initialValues) => {
        setAttemptedSubmit(true);
        setIsSaving(true);

        const formData = new FormData();

        const openingHoursData = Object.entries(values.openingHours).map(
            ([weekday, { openingTime, closingTime }]) => {
                return {
                    weekday: weekday,
                    startTime: openingTime,
                    endTime: closingTime
                };
            }
        );

        const weekdaySchedule = values.seasonalEnabled
            ? openingHoursData
            : [];


        const dietaryRestrictions = Object.entries(values.dietaryRestrictions)
            .filter(([, v]) => v)
            .map(([k]) => k.toUpperCase());


        console.log("dietary: " + dietaryRestrictions);

        // Flatten UI groups back to the flat variant list the backend expects: the group name
        // becomes each variant's `category`, and the group's rules are copied onto every variant.
        const mappedVariants = groups.flatMap(g =>
            g.items.map(it => ({
                name: it.name,
                // Amount is optional and free-text; only send it when it parses to a number.
                size: it.size !== '' && !Number.isNaN(Number(it.size)) ? Number(it.size) : undefined,
                price: Number(it.price),
                posId: it.posId || undefined,
                category: g.name || undefined,
                isRequired: !!g.isRequired,
                isSingleChoice: g.isSingleChoice !== false
            }))
        );

        const dateScheduleRequest = values.specificDateEnabled
            ? {
                startDate: values.startSeason,
                endDate: values.endSeason
            }
            : {
                startDate: '',
                endDate: ''
            };

        const request = {
            id: isEditMode ? Number(itemId) : undefined,
            name: values.name,
            description: values.description,
            baseSize: Number(values.size),
            basePrice: Number(values.price),
            unit: values.unit,
            categoryId: values.category,
            locationId: Number(id),
            dietaryRestrictions,
            variants: mappedVariants,
            weekdaySchedule,
            dateScheduleRequest,
            suggestedProducts: linkedProducts.map(p => ({ inventoryItemId: p.id })),
            posId: values.posId || undefined
        };
        console.log('requestPayload' + JSON.stringify(request));
        console.log("adsfasdf: " + JSON.stringify(linkedProducts.map(p => p.id)));

        const jsonBlob = new Blob(
            [JSON.stringify(request)],
            { type: "application/json" }
        );
        formData.append("request", jsonBlob);

        if (fileBlob) {
            formData.append("file", fileBlob);
        } else {
            formData.append("file", new Blob());
        }
        try {
            if (isEditMode) {
                await restaurantItemService.updateItem(formData);
                // await restaurantItemService.addSuggestedProduct({
                //     menuItemId: Number(itemId),
                //     suggestedProductIds: linkedProducts.map(p => p.id)
                // });
                notify('Item updated successfully', { type: 'success' });
            } else {
                await restaurantItemService.createItem(formData);
                notify('Item created successfully', { type: 'success' });
            }
            setIsSaving(false);
            redirect("/locationsManagement/" + id + "/items");
        }
        catch (error) {
            notify(error.response.data.error, { type: 'error' });
            console.error(error.response.data.error);
            setIsSaving(false);
            Sentry.captureException(error);
        }
    };

    if ((isEditMode && isLoading)) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Formik
                initialValues={initialEditValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ values, handleChange, errors, touched, isValid, dirty, setFieldValue, handleBlur }) => (
                    <Form>
                        <Box
                            sx={{
                                px: 4,
                                py: 4,
                                display: 'flex',
                                gap: 'var(--space-32)',
                                flexDirection: 'row'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 0.5,
                                }}
                            >
                                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{
                                    justifyContent: 'space-between',
                                    mb: 'var(--space-24)'
                                }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: 'var(--text-sm)',
                                            mb: 2,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {t('locationsManagement.createItem.seasonalSchedule')}
                                    </Typography>
                                    <Switch
                                        name="seasonalEnabled"
                                        onChange={handleChange}
                                        color="primary"
                                        size="small"
                                    />
                                </Stack>
                                <Stack spacing={3}>
                                    {days.map((day) => (
                                        <Stack
                                            key={day.id}
                                            direction="row"
                                            alignItems="center"
                                            spacing={2}
                                            justifyContent="space-between"
                                        >
                                            <Typography
                                                sx={{
                                                    flex: 1,
                                                    fontSize: 'var(--text-xs)',
                                                    color: 'text.primary',
                                                    minWidth: 80,
                                                }}
                                            >
                                                {day.name}
                                            </Typography>

                                            <TextField
                                                type="time"
                                                size="small"
                                                name={`openingHours.${day.id}.openingTime`}
                                                disabled={!values.seasonalEnabled}
                                                value={values.openingHours[day.id].openingTime}
                                                onChange={handleChange}
                                            />
                                            <TextField
                                                type="time"
                                                size="small"
                                                name={`openingHours.${day.id}.closingTime`}
                                                disabled={!values.seasonalEnabled}
                                                value={values.openingHours[day.id].closingTime}
                                                onChange={handleChange}
                                            />
                                        </Stack>
                                    ))}
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{
                                        justifyContent: 'space-between',
                                        mb: 'var(--space-24)'
                                    }}>
                                        <Typography sx={{
                                            fontWeight: 600,
                                            fontSize: 'var(--text-sm)',
                                            mb: 2,
                                            color: 'text.secondary',
                                        }}>
                                            {t('locationsManagement.createItem.specificDate')}
                                        </Typography>
                                        <Switch
                                            name="specificDateEnabled"
                                            onChange={handleChange}
                                            color="primary"
                                            size="small"
                                        />

                                    </Stack>
                                    <Stack spacing={3} sx={{ mt: '-10px' }}>
                                        <TextField
                                            disabled={!values.specificDateEnabled}
                                            name="startSeason"
                                            onChange={handleChange}
                                            placeholder="dd.mm.yyyy"
                                            type="date"
                                            fullWidth
                                            size="small"
                                        />

                                        <TextField
                                            disabled={!values.specificDateEnabled}
                                            name="endSeason"
                                            onChange={handleChange}
                                            placeholder="dd.mm.yyyy"
                                            type="date"
                                            fullWidth
                                            size="small"
                                        />
                                    </Stack>
                                </Stack>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                }}
                            >
                                <Stack spacing={0}>
                                    <FormControl
                                        size="small"
                                        fullWidth
                                        error={(touched.language || attemptedSubmit) && Boolean(errors.language)}>
                                        <InputLabel>{t('locationsManagement.create.language')}</InputLabel>
                                        <Select
                                            name="language"
                                            value={values.language}
                                            onBlur={handleBlur}
                                            onChange={(e) => {
                                                handleChange(e);

                                                const langId = e.target.value;
                                                const langName = LANGUAGE.find(l => l.id === langId)?.id;
                                                if (translated && langName) {
                                                    const match = translations.find(t => t.language === langName);

                                                    if (match) {
                                                        setFieldValue("name", match.translatedName);
                                                        setFieldValue("description", match.translatedDescription);
                                                    }
                                                }
                                            }}
                                        >
                                            <MenuItem value="" disabled>
                                                {t('locationsManagement.create.language')}
                                            </MenuItem>
                                            {LANGUAGE.map(c => (
                                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                                            ))}
                                        </Select>

                                        <FormHelperText>
                                            {touched.language && errors.language ? errors.language : " "}
                                        </FormHelperText>
                                    </FormControl>

                                    <TextField
                                        name="name"
                                        size="small"
                                        placeholder={t('locationsManagement.createItem.name')}
                                        fullWidth
                                        value={values.name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={(touched.name || attemptedSubmit) && Boolean(errors.name)}
                                        helperText={(touched.name || attemptedSubmit) && errors.name ? errors.name : " "}
                                    />

                                    <TextField
                                        name="posId"
                                        size="small"
                                        placeholder="POS ID"
                                        fullWidth
                                        value={values.posId}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        helperText=" "
                                    />

                                    <FormControl
                                        size="small"
                                        fullWidth
                                        error={(touched.category || attemptedSubmit) && Boolean(errors.category)}>
                                        <InputLabel>{t('locationsManagement.createItem.category')}</InputLabel>
                                        <Select
                                            name="category"
                                            value={values.category}
                                            onBlur={handleBlur}
                                            onChange={handleChange}>
                                            <MenuItem value="" disabled>
                                                {t('locationsManagement.createItem.category')}
                                            </MenuItem>
                                            {categories.map((cat) => (
                                                <MenuItem key={cat.id} value={cat.id}>
                                                    {cat.name}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        <FormHelperText>
                                            {touched.category && errors.category ? errors.category : " "}
                                        </FormHelperText>
                                    </FormControl>

                                    <TextField
                                        name="price"
                                        size="small"
                                        placeholder={t('locationsManagement.createItem.price')}
                                        fullWidth
                                        value={values.price}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={(touched.price || attemptedSubmit) && Boolean(errors.price)}
                                        helperText={touched.price && errors.price ? errors.price : " "}
                                    />

                                    <FormControl
                                        size="small"
                                        fullWidth
                                        error={(touched.language || attemptedSubmit) && Boolean(errors.language)}>
                                        <InputLabel>{t('locationsManagement.createItem.unitType')}</InputLabel>
                                        <Select
                                            name="unit"
                                            value={values.unit}
                                            onBlur={handleBlur}
                                            onChange={handleChange}>
                                            {UNITS.map(u => (
                                                <MenuItem key={u.id} value={u.id}>
                                                    {u.name}
                                                </MenuItem>
                                            ))}
                                        </Select>

                                        <FormHelperText>
                                            {touched.unit && errors.unit ? errors.unit : " "}
                                        </FormHelperText>
                                    </FormControl>
                                    <TextField
                                        name="size"
                                        placeholder={t('locationsManagement.createItem.amount')}
                                        fullWidth
                                        slotProps={{ htmlInput: { maxLength: 400 } }}
                                        value={values.size}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={(touched.size || attemptedSubmit) && Boolean(errors.size)}
                                        helperText={touched.size && errors.size ? errors.size : " "}
                                        sx={{
                                            '& .MuiInputBase-root':
                                                { padding: 0 }
                                        }}
                                    />
                                </Stack>
                                <TextField
                                    name="description"
                                    label={t('locationsManagement.create.description')}
                                    disabled={(!values.language || translated || isTranslating) && !isEditMode}
                                    multiline
                                    rows={4}
                                    fullWidth
                                    slotProps={{ htmlInput: { maxLength: 400 } }}
                                    value={values.description}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    error={(touched.description || attemptedSubmit) && Boolean(errors.description)}
                                    helperText={touched.description && errors.description ? errors.description : ""}
                                    sx={{
                                        '& .MuiInputBase-root':
                                            { padding: 0 }
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    flex: 1,
                                    gap: 'var(--space-24)',
                                    alignItems: 'center',
                                }}
                            >
                                <Box
                                    onDrop={handleDrop}
                                    onDragOver={(e) => e.preventDefault()}
                                    sx={{
                                        width: '100%',
                                        height: 360,
                                        border: '1px dashed var(--color-secondary)',
                                        borderRadius: 'var(--radius-4)',
                                        backgroundColor: 'var(--color-background-alt)',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: 'text.secondary',
                                        fontSize: 'var(--text-xs)',
                                        cursor: 'pointer',
                                        transition: 'background-color 0.2s ease',
                                        '&:hover': {
                                            backgroundColor: 'var(--color-background-drop)',
                                        },
                                    }}
                                    onClick={() => document.getElementById('fileInput')?.click()}
                                >
                                    <input
                                        id="fileInput"
                                        type="file"
                                        accept="image/*"
                                        hidden
                                        onChange={handleFileSelect}
                                    />

                                    {previewUrl ? (
                                        <Box sx={{ textAlign: 'center' }}>
                                            <img
                                                src={previewUrl}
                                                alt="preview"
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '360px',
                                                    overflow: 'hidden',
                                                    borderRadius: '4px',
                                                    objectFit: 'fill',
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <>
                                            <Typography sx={{ mb: 0.5 }}>{t('locationsManagement.create.dropFileHere')}</Typography>
                                        </>
                                    )}
                                </Box>

                                {fileBlob && (
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        sx={{ mt: 2 }}
                                    >
                                        <IconButton
                                            icon={<EditIcon />}
                                            onClick={() => document.getElementById('fileInput')?.click()}
                                        />

                                        <IconButton
                                            icon={<DeleteOutlineIcon />}
                                            onClick={handleRemove}
                                        />
                                    </Stack>
                                )}
                                <Box sx={{ width: '100%' }}>
                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                        <Typography sx={{ fontWeight: 600, fontSize: 'var(--text-sm)', color: 'text.secondary' }}>
                                            {t('locationsManagement.createItem.variantGroups')}
                                        </Typography>
                                        <Button
                                            type="button"
                                            startIcon={<AddIcon />}
                                            onClick={openAddGroup}
                                            sx={{ textTransform: 'none', color: 'var(--color-primary)' }}
                                        >
                                            {t('locationsManagement.createItem.addVariantGroup')}
                                        </Button>
                                    </Stack>

                                    {groups.length === 0 ? (
                                        <Typography sx={{ fontSize: 'var(--text-xs)', color: 'text.secondary', py: 2 }}>
                                            {t('locationsManagement.createItem.noVariantGroups')}
                                        </Typography>
                                    ) : (
                                        <Stack spacing={2}>
                                            {groups.map(group => (
                                                <Box
                                                    key={group.key}
                                                    sx={{
                                                        width: '100%',
                                                        border: '1px solid var(--color-secondary)',
                                                        borderRadius: 'var(--radius-6)',
                                                        p: 2,
                                                    }}
                                                >
                                                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
                                                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                                                            <Typography sx={{ fontWeight: 600, fontSize: 'var(--text-sm)' }}>
                                                                {group.name || t('locationsManagement.createItem.legacyGroupName')}
                                                            </Typography>
                                                            <Chip
                                                                size="small"
                                                                label={group.isSingleChoice
                                                                    ? t('locationsManagement.createItem.singleChoice')
                                                                    : t('locationsManagement.createItem.multipleChoice')}
                                                            />
                                                            {group.isRequired && (
                                                                <Chip
                                                                    size="small"
                                                                    label={t('locationsManagement.createItem.required')}
                                                                    sx={{ backgroundColor: 'var(--color-primary)', color: '#fff' }}
                                                                />
                                                            )}
                                                        </Stack>
                                                        <Stack direction="row" spacing={0.5}>
                                                            <IconButton
                                                                icon={<EditIcon fontSize="small" />}
                                                                onClick={() => openEditGroup(group)}
                                                            />
                                                            <IconButton
                                                                icon={<DeleteOutlineIcon fontSize="small" sx={{ color: 'var(--color-error)' }} />}
                                                                onClick={() => handleDeleteGroup(group.key)}
                                                            />
                                                        </Stack>
                                                    </Stack>

                                                    {!group.name && (
                                                        <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'var(--color-error)', mb: 1 }}>
                                                            {t('locationsManagement.createItem.legacyGroupHint')}
                                                        </Typography>
                                                    )}

                                                    <Stack spacing={0.5}>
                                                        {group.items.map((it, i) => (
                                                            <Stack
                                                                key={i}
                                                                direction="row"
                                                                justifyContent="space-between"
                                                                sx={{ color: 'text.secondary' }}
                                                            >
                                                                <Typography sx={{ fontSize: 'var(--text-xs)' }}>
                                                                    {it.name}{it.size ? ` · ${it.size}` : ''}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: 'var(--text-xs)' }}>{it.price}</Typography>
                                                            </Stack>
                                                        ))}
                                                    </Stack>
                                                </Box>
                                            ))}
                                        </Stack>
                                    )}
                                </Box>


                                <Box sx={{ width: '100%' }}>
                                    <Typography
                                        sx={{
                                            fontWeight: 600,
                                            fontSize: 'var(--text-sm)',
                                            mb: 1,
                                            color: 'text.secondary',
                                        }}
                                    >
                                        {t('locationsManagement.createItem.linkedProducts')}
                                    </Typography>

                                    <Autocomplete
                                        sx={{
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                border: '1px solid var(--color-secondary) !important',
                                                borderRadius: 'var(--radius-6)',
                                            },
                                            '& .MuiInputBase-input': {
                                                border: '0 !important',
                                                padding: '12px 12px !important',
                                            },
                                            '& .MuiInputBase-root': {
                                                padding: '0 !important',
                                            },
                                        }}
                                        multiple
                                        options={availableProducts}
                                        getOptionLabel={(option) => option.name}
                                        value={linkedProducts}
                                        loading={productsLoading}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        onChange={(_, newValue) => setLinkedProducts(newValue)}
                                        renderTags={() => null}
                                        renderInput={(params) => {
                                            const { InputProps: autocompleteInputProps, ...restParams } = params;
                                            return (
                                                <TextField
                                                    {...restParams}
                                                    placeholder={t('locationsManagement.createItem.searchProducts')}
                                                    size="small"
                                                    slotProps={{
                                                        input: {
                                                            ...autocompleteInputProps,
                                                            endAdornment: (
                                                                <>
                                                                    {productsLoading ? (
                                                                        <CircularProgress color="inherit" size={20} />
                                                                    ) : null}
                                                                    {autocompleteInputProps.endAdornment}
                                                                </>
                                                            ),
                                                        },
                                                    }}
                                                />
                                            );
                                        }}
                                    />

                                    {linkedProducts.length > 0 && (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                                            {linkedProducts.map((product) => (
                                                <Chip
                                                    key={product.id}
                                                    label={product.name}
                                                    size="small"
                                                    onDelete={() =>
                                                        setLinkedProducts((prev) =>
                                                            prev.filter((p) => p.id !== product.id)
                                                        )
                                                    }
                                                    sx={{
                                                        backgroundColor: 'var(--color-primary)',
                                                        color: '#fff',
                                                        '& .MuiChip-deleteIcon': {
                                                            color: '#fff',
                                                            '&:hover': { color: '#ddd' },
                                                        },
                                                    }}
                                                />
                                            ))}
                                        </Box>
                                    )}
                                </Box>



                            </Box>
                        </Box>
                        <Box
                            sx={{
                                px: 4,
                                display: 'flex',
                                gap: 'var(--space-24)',
                                justifyContent: 'start',
                                flexDirection: 'row',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    fontSize: 'var(--text-sm)',
                                    mb: 2,
                                    color: 'text.secondary',
                                }}
                            >
                                {t('locationsManagement.createItem.dietaryRestrictions')}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                px: 4,
                                display: 'flex',
                                flexDirection: 'row',
                                gap: 'var(--space-24)',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                mb: 'var(--space-32)',
                            }}
                        >
                            <Stack direction="row" alignItems="center" spacing={1} >
                                <Typography
                                    sx={{
                                        fontSize: 'var(--text-xxs)',
                                        color: 'text.secondary',
                                        minWidth: 'fit-content'
                                    }}
                                >
                                    {t('locationsManagement.createItem.spicy')}
                                </Typography>

                                <Switch
                                    name="dietaryRestrictions.spicy"
                                    checked={values.dietaryRestrictions.spicy}
                                    onChange={handleChange}
                                    color="primary"
                                    size="small"
                                />
                            </Stack>

                            <Stack direction="row" alignItems="center" spacing={1} >
                                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary' }}>
                                    {t('locationsManagement.createItem.dairyFree')}
                                </Typography>

                                <Switch
                                    name="dietaryRestrictions.dairy_free"
                                    checked={values.dietaryRestrictions.dairy_free}
                                    onChange={handleChange}
                                    color="primary"
                                    size="small"
                                />
                            </Stack>

                            <Stack direction="row" alignItems="center" spacing={1} >
                                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary' }}>
                                    {t('locationsManagement.createItem.glutenFree')}
                                </Typography>

                                <Switch
                                    name="dietaryRestrictions.gluten_free"
                                    checked={values.dietaryRestrictions.gluten_free}
                                    onChange={handleChange}
                                    color="primary"
                                    size="small"
                                />
                            </Stack>

                            <Stack direction="row" alignItems="center" spacing={1} >
                                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary' }}>
                                    {t('locationsManagement.createItem.vegan')}
                                </Typography>

                                <Switch
                                    name="dietaryRestrictions.vegan"
                                    checked={values.dietaryRestrictions.vegan}
                                    onChange={handleChange}
                                    color="primary"
                                    size="small"
                                />
                            </Stack>

                            <Stack direction="row" alignItems="center" spacing={1} >
                                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary' }}>
                                    {t('locationsManagement.createItem.vegetarian')}
                                </Typography>

                                <Switch
                                    name="dietaryRestrictions.vegetarian"
                                    checked={values.dietaryRestrictions.vegetarian}
                                    onChange={handleChange}
                                    color="primary"
                                    size="small"
                                />
                            </Stack>

                            <Stack direction="row" alignItems="center" spacing={1} >
                                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary' }}>
                                    {t('locationsManagement.createItem.kosher')}
                                </Typography>

                                <Switch
                                    name="dietaryRestrictions.kosher"
                                    checked={values.dietaryRestrictions.kosher}
                                    onChange={handleChange}
                                    color="primary"
                                    size="small"
                                />
                            </Stack>

                            <Stack direction="row" alignItems="center" spacing={1} >
                                <Typography sx={{ fontSize: 'var(--text-xxs)', color: 'text.secondary' }}>
                                    {t('locationsManagement.createItem.halal')}
                                </Typography>

                                <Switch
                                    name="dietaryRestrictions.halal"
                                    checked={values.dietaryRestrictions.halal}
                                    onChange={handleChange}
                                    color="primary"
                                    size="small"
                                />
                            </Stack>
                        </Box>
                        <Box
                            sx={{
                                px: 4,
                                display: 'flex',
                                marginBottom: 'var(--space-32)',
                                gap: 'var(--space-24)',
                                justifyContent: 'end',
                                flexDirection: 'row',
                            }}
                        >
                            <Button
                                type="button"
                                variant="contained"
                                disabled={isTranslating || !values.language || !values.description || (translated && !isEditMode)}
                                sx={{
                                    borderRadius: 0,
                                    backgroundColor: 'var(--color-primary)',
                                    boxShadow: 'none',
                                    alignSelf: 'flex-end',
                                    width: '95px',
                                    '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                                }}
                                onClick={async () => {
                                    try {
                                        setIsTranslating(true);
                                        const payload = {
                                            locationId: id,
                                            itemName: values.name,
                                            itemDescription: values.description,
                                        };
                                        console.log(payload);
                                        const response = await translateService.translateItem(payload);

                                        const languageNameToId = Object.fromEntries(
                                            LANGUAGE.map(l => [l.name, l.id])
                                        );

                                        const mappedResponse = response.map(t => ({
                                            ...t,
                                            language: languageNameToId[t.language] || t.language
                                        }));

                                        const fullTranslations = [
                                            { language: values.language, translatedName: values.name, translatedDescription: values.description },
                                            ...mappedResponse
                                        ];
                                        setTranslations(fullTranslations);

                                        const match = fullTranslations.find(t => t.language === values.language);

                                        if (match) {
                                            setFieldValue("name", match.translatedName);
                                            setFieldValue("description", match.translatedDescription);
                                        }

                                        setIsTranslating(false);
                                        setTranslated(true);
                                        notify('Translation completed', { type: 'success' });
                                    } catch (error) {
                                        notify(error.response.data.error, { type: 'error' });
                                        console.error(error.response.data.error);
                                        setIsTranslating(false);
                                        Sentry.captureException(error);
                                    }
                                }}

                            >
                                {isTranslating ? <CircularProgress size={25} sx={{ color: 'white' }} /> : t('locationsManagement.create.translate')}
                            </Button>
                            <Button
                                variant="outlined"
                                type="button"
                                sx={{ borderRadius: 0, borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
                            >
                                {t('common.cancel')}
                            </Button>

                            <Button
                                type="submit"
                                variant="contained"
                                // disabled={!translated}
                                sx={{
                                    borderRadius: 0,
                                    backgroundColor: 'var(--color-primary)',
                                    boxShadow: 'none',
                                    alignSelf: 'flex-end',
                                    width: '127px',
                                    '&:hover': { cursor: 'pointer', backgroundColor: 'var(--color-primary-hover)', boxShadow: 'var(--shadow-1)' },
                                }}
                            >
                                {isSaving ? <CircularProgress size={25} sx={{ color: 'white' }} /> : t('common.saveChanges')}
                            </Button>
                        </Box>
                    </Form>
                )}
            </Formik>

            <VariantGroupDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleSaveGroup}
                initialGroup={editingGroup}
                makeKey={makeGroupKey}
            />
        </>
    );
}
